import { createAsyncThunk } from '@reduxjs/toolkit';
import { AppServerResponse } from '../../utils/interfaces/generalInterfaces';
import axios from 'axios';
import { BASE_API_URL } from '../../utils/constants';
import { logoutUser } from './authService';
import { myFirebaseAuth } from '../../firebase/config';
import { RootState } from '../store';
import { BuyerMonthlyAnalytics, BuyerStatementData, BuyerUserPermission } from '@dill/dill-shared';

export const getGroupedBuyersSuppliers = createAsyncThunk(
  'buyers/getGroupedBuyersSuppliers',
  async ({ mainBuyerId, weeksLimit }: { mainBuyerId: string; weeksLimit: number }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      let url = `${BASE_API_URL}/mainBuyers/${mainBuyerId}/buyers/getSuppliersForBuyer`;
      if (weeksLimit) {
        url = `${BASE_API_URL}/mainBuyers/${mainBuyerId}/buyers/getSuppliersForBuyer?weeksLimit=${weeksLimit}`;
      }
      const response = await axios.get<AppServerResponse>(url, {
        headers: {
          Authorization: 'Bearer ' + token,
        },
      });

      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const getBasicSupplier = createAsyncThunk(
  'buyers/getBasicSupplier',
  async ({ supplierDomainId }: { supplierDomainId: string }, thunkApi) => {
    try {
      if (!supplierDomainId) {
        return null;
      }
      const response = await axios.get<AppServerResponse>(
        BASE_API_URL + `/suppliers/getBasicSupplier/${supplierDomainId}`
      );

      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const getInvoicePdf = createAsyncThunk(
  'buyers/getInvoicePdf',
  async (
    {
      supplierDomainId,
      invoiceId,
      supplierId,
      preview = false,
      print = false, // Added print option
    }: {
      supplierDomainId?: string;
      supplierId?: string;
      invoiceId: string;
      preview?: boolean;
      print?: boolean;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<any>(
        `${BASE_API_URL}/invoicesAPI/invoicePdf/?invoiceId=${invoiceId}${
          supplierDomainId ? `&supplierDomainId=${supplierDomainId}` : ''
        }${supplierId ? `&supplierId=${supplierId}` : ''}`,
        {
          responseType: 'blob',
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      const file = new Blob([response.data], { type: 'application/pdf' });
      const link = document.createElement('a');

      link.href = window.URL.createObjectURL(file);
      if (print) {
        // Open the PDF in a new window and trigger the print dialog
        const pdfWindow = window.open(link.href);
        if (pdfWindow) {
          pdfWindow.onload = () => {
            pdfWindow.print();
          };
        }
      } else if (!preview) {
        link.download = `invoice-${invoiceId}-${+new Date()}.pdf`;
        link.click();
      }

      return { success: true, data: { pdfUrl: link.href } };
    } catch (error: any) {
      console.log(error);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const getDocumentPdf = createAsyncThunk(
  'buyers/getDocumentPdf',
  async (
    {
      supplierDomainId,
      id,
      type,
      supplierId,
      preview = false,
      print = false, // Added print option
    }: {
      supplierDomainId?: string;
      supplierId?: string;
      id: string;
      type: 'INVOICE' | 'CREDIT_MEMO';
      preview?: boolean;
      print?: boolean;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<any>(
        `${BASE_API_URL}/invoicesAPI/documentPdf`,

        {
          params: {
            supplierDomainId,
            id,
            type,
            supplierId,
          },
          responseType: 'blob',
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      const file = new Blob([response.data], { type: 'application/pdf' });
      const link = document.createElement('a');

      link.href = window.URL.createObjectURL(file);
      if (print) {
        // Open the PDF in a new window and trigger the print dialog
        const pdfWindow = window.open(link.href);
        if (pdfWindow) {
          pdfWindow.onload = () => {
            pdfWindow.print();
          };
        }
      } else if (!preview) {
        link.download = `${type}-${id}-${+new Date()}.pdf`;
        link.click();
      }

      return { success: true, data: { pdfUrl: link.href } };
    } catch (error: any) {
      console.log(error);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);
export const getDocumentPdfAttachments = createAsyncThunk(
  'buyers/getDocumentPdfAttachments',
  async (
    {
      id,
      supplierDomainId,
    }: {
      supplierDomainId: string;
      id: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<any>(
        `${BASE_API_URL}/invoicesAPI/documentPdfAttachments`,

        {
          params: {
            id,
            supplierDomainId,
          },
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      return { success: true, data: response.data };
    } catch (error: any) {
      console.log(error);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const downloadMultiInvoicePdfZip = createAsyncThunk(
  'buyers/downloadMultiInvoicePdfZip',
  async (
    {
      supplierDomainId,
      supplierId,
      invoiceIds,
      combined = false,
    }: { supplierDomainId?: string; supplierId?: string; invoiceIds: string[]; combined?: boolean },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.post<any>(
        `${BASE_API_URL}/invoicesAPI/multiInvoicePdf`,
        {
          invoiceIds: invoiceIds,
          ...(supplierDomainId ? { supplierDomainId } : {}),
          ...(supplierId ? { supplierId } : {}),
          combined,
        },
        {
          responseType: 'blob',
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      if (!combined) {
        const file = new Blob([response.data], { type: 'application/zip' });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(file);
        link.download = `zip-${invoiceIds.length}-${supplierDomainId || supplierId}`;
        link.click();
      } else {
        const file = new Blob([response.data], { type: 'application/pdf' });
        const link = document.createElement('a');
        link.download = `invoice-${supplierDomainId || supplierId}-${new Date().getTime()}.pdf`;

        link.href = window.URL.createObjectURL(file);
        link.click();
      }

      return { success: true, data: {} };
    } catch (error: any) {
      console.log(error);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const downloadInvoicePdfReceipt = createAsyncThunk(
  'buyers/downloadInvoicePdfReceipt',
  async (
    {
      supplierId,
      transactionId,
      preview = false,
      supplierDomainId,
    }: { supplierId?: string; transactionId: string; preview?: boolean; supplierDomainId?: string },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<any>(`${BASE_API_URL}/invoicesAPI/receiptPdf`, {
        responseType: 'blob',
        headers: {
          Authorization: 'Bearer ' + token,
        },
        params: { supplierId, transactionId, supplierDomainId },
      });
      const file = new Blob([response.data], { type: 'application/pdf' });
      const link = document.createElement('a');

      link.href = window.URL.createObjectURL(file);
      if (!preview) {
        link.download = `receipt-${transactionId}.pdf`;
        link.click();
      }

      return { success: true, data: { pdfUrl: link.href } };
    } catch (error: any) {
      console.log(error);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const getMainBuyer = createAsyncThunk(
  'buyers/getMainBuyer',
  async ({ mainBuyerId }: { mainBuyerId: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      console.log('token', token);
      if (!token) {
        return null;
      }
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/mainBuyers/${mainBuyerId}`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error);
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const addSendgridApiKeyToMainBuyer = createAsyncThunk(
  'buyers/addSendgridApiKeyToMainBuyer',
  async (
    { mainBuyerId, sendgridApiKey }: { mainBuyerId: string; sendgridApiKey: string },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      console.log('token', token);
      if (!token) {
        return null;
      }
      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/mainBuyers/${mainBuyerId}/addSendGridKey`,
        { sendgridApiKey },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error);
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const supplierInviteUsersForBuyer = createAsyncThunk(
  'buyers/supplierInviteUsersForBuyer',
  async (
    {
      supplierId,
      buyerId,
      invitedUsers,
    }: {
      supplierId: string;
      buyerId: string;
      invitedUsers: {
        firstName: string;
        lastName: string;
        email: string;
        title?: string;
        permissions?: BuyerUserPermission[];
      }[];
    },
    thunkApi
  ): Promise<any> => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/supplierBuyers/inviteUsers`,
        {
          buyerId,
          invitedUsers,
        },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.data) {
        return thunkApi.rejectWithValue(error.response.data);
      }
      return thunkApi.rejectWithValue(error);
    }
  }
);

export const supplierResendBuyerUserInvite = createAsyncThunk(
  'buyers/supplierResendBuyerUserInvite',
  async (
    {
      supplierId,
      buyerId,
      userId,
    }: {
      supplierId: string;
      buyerId: string;
      userId: string;
    },
    thunkApi
  ): Promise<any> => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/supplierBuyers/resendUserInvite/${buyerId}/${userId}`,
        {},
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.data) {
        return thunkApi.rejectWithValue(error.response.data);
      }
      return thunkApi.rejectWithValue(error);
    }
  }
);

export const mainBuyerInviteUsersForBuyer = createAsyncThunk(
  'buyers/mainBuyerInviteUsersForBuyer',
  async (
    {
      mainBuyerId,
      invitedUsers,
    }: {
      mainBuyerId: string;
      invitedUsers: {
        firstName: string;
        lastName: string;
        email: string;
        title?: string;
        permissions?: BuyerUserPermission[];
      }[];
    },
    thunkApi
  ): Promise<any> => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/mainBuyers/${mainBuyerId}/buyers/inviteUsers`,
        {
          invitedUsers,
        },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.data) {
        return thunkApi.rejectWithValue(error.response.data);
      }
      return thunkApi.rejectWithValue(error);
    }
  }
);
export const updateBuyerAutopay = createAsyncThunk(
  'buyers/updateBuyerAutopay',
  async (
    {
      mainBuyerId,
      buyerId,
      isAutopayActive,
      autopayDetails,
    }: {
      mainBuyerId: string;
      buyerId: string;
      isAutopayActive: boolean;
      autopayDetails: {
        paymentMethodId: string;
        userId?: string;
        frequency?: 'weekly' | 'monthly' | 'dueDate';
        frequencyOn?: string;
        paymentStrategy?: 'FLAT_AMOUNT' | 'ALL_OPEN' | 'ALL_PAST_DUE' | 'PRIOR_MONTH_STATEMENT';
        flatAmount?: number | null;
      };
    },
    thunkApi
  ) => {
    try {
      const state = thunkApi.getState() as RootState;
      const userId = state.auth.user?.id ?? '';
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.put<AppServerResponse>(
        `${BASE_API_URL}/mainBuyers/${mainBuyerId}/buyers/updateAutoPay/${buyerId}`,
        {
          isAutopayActive,
          autopayDetails: { ...autopayDetails, userId },
        },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      let errorMessage = '';
      if (error?.response?.data?.errors) {
        errorMessage = [...error?.response?.data?.errors].reduce((prev, curr) => {
          if (curr.msg) {
            return `${prev} ${curr.msg}`;
          }
          return prev;
        }, '');
      } else {
        errorMessage = error?.message;
      }
      return thunkApi.rejectWithValue(errorMessage);
    }
  }
);

export const getBuyerAndMainBuyerDetails = createAsyncThunk(
  'buyers/getBuyerAndMainBuyerDetails',
  async ({ mainBuyerId }: { mainBuyerId: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      console.log('token', token);
      if (!token) {
        return null;
      }
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/mainBuyers/${mainBuyerId}/buyers/getBuyerAndMainBuyerDetails`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error);
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const getBuyerStatementPeriods = createAsyncThunk(
  'buyers/getBuyerStatementPeriods',
  async ({ mainBuyerId }: { mainBuyerId: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/mainBuyers/${mainBuyerId}/invoices/listBuyerStatementPeriods`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const getStatementDetails = createAsyncThunk(
  'buyers/getStatementDetails',
  async ({ mainBuyerId, month }: { mainBuyerId: string; month: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/mainBuyers/${mainBuyerId}/invoices/buyerStatementData?month=${month}`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const getJobFilteredStatementPdf = createAsyncThunk(
  'buyers/getJobFilteredStatementPdf',
  async (
    {
      mainBuyerId,
      statementId,
      invoiceIds,
      creditMemoIds,
    }: {
      mainBuyerId: string;
      statementId: string;
      invoiceIds: string[];
      creditMemoIds: string[];
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const params = new URLSearchParams({ statementId, invoiceIds: invoiceIds.join(',') });
      if (creditMemoIds.length > 0) params.set('creditMemoIds', creditMemoIds.join(','));

      const response = await axios.get<any>(
        `${BASE_API_URL}/mainBuyers/${mainBuyerId}/invoices/downloadJobFilteredStatement?${params}`,
        {
          responseType: 'blob',
          headers: { Authorization: 'Bearer ' + token },
        }
      );
      const blobUrl = window.URL.createObjectURL(
        new Blob([response.data], { type: 'application/pdf' })
      );
      return { success: true, data: { pdfUrl: blobUrl } };
    } catch (error: any) {
      console.error('getJobFilteredStatementPdf error', error);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const fetchBuyerInvoiceVolume = createAsyncThunk(
  'buyers/fetchBuyerInvoiceVolume',
  async (
    {
      mainBuyerId,
      startDate,
      endDate,
    }: { mainBuyerId: string; startDate: string; endDate: string },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<
        AppServerResponse & {
          data: {
            supplierName: string;
            months: { month: string; totalInvoiced: number; invoiceCount: number }[];
            years: { year: string; totalInvoiced: number; invoiceCount: number }[];
            totalInvoiced: number;
            invoiceCount: number;
          };
        }
      >(
        `${BASE_API_URL}/mainBuyers/${mainBuyerId}/invoices/invoiceVolume?startDate=${startDate}&endDate=${endDate}`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error?.response?.data?.errors?.[0]?.msg || error.message);
    }
  }
);

export const fetchBuyerMonthlyAnalytics = createAsyncThunk(
  'buyers/fetchBuyerMonthlyAnalytics',
  async (
    {
      mainBuyerId,
      buyerId,
      months = 12,
    }: { mainBuyerId: string; buyerId: string; months?: number },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<
        AppServerResponse & { data: { monthlyAnalytics: BuyerMonthlyAnalytics[] } }
      >(`${BASE_API_URL}/mainBuyers/${mainBuyerId}/buyers/getMonthlyAnalytics?months=${months}`, {
        headers: {
          Authorization: 'Bearer ' + token,
        },
      });
      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const deleteBuyerUser = createAsyncThunk(
  'buyers/deleteBuyerUser',
  async ({ mainBuyerId, userId }: { mainBuyerId: string; userId: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.delete<AppServerResponse>(
        `${BASE_API_URL}/mainBuyers/${mainBuyerId}/buyers/deleteUser/${userId}`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.data?.errors?.[0]?.msg) {
        return thunkApi.rejectWithValue(error.response.data.errors[0].msg);
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const updateBuyerUserPermissions = createAsyncThunk(
  'buyers/updateBuyerUserPermissions',
  async (
    {
      mainBuyerId,
      userId,
      permissions,
    }: { mainBuyerId: string; userId: string; permissions: BuyerUserPermission[] },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.put<AppServerResponse>(
        `${BASE_API_URL}/mainBuyers/${mainBuyerId}/buyers/updateUserPermissions/${userId}`,
        { permissions },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.data?.errors?.[0]?.msg) {
        return thunkApi.rejectWithValue(error.response.data.errors[0].msg);
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);
